import { RowCrudStatus } from "../../common/types";
import {
  EmailVerification,
  EmailVerificationCreateMutationVariables,
  EmailVerificationUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { EmailVerificationStatus } from "../../../api/graphql";
import { EmailVerificationScope } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";

export type EmailVerificationValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  email: string;
  status: EmailVerificationStatus;
  scope: EmailVerificationScope | "";
  expiresAt: Date | null;
  sentAt: Date | null;
  verifiedAt: Date | null;
  sentCount: number;
  lastSentAt: Date | null;
  __requestedBy: string;
  showRequestedBy: { displayId: string; id: string } | null;
  sourceEntity: string;
  sourceRecordId: string;
  validationChanges: string;
  validationSubmittedAt: Date | null;
};

export type EmailVerificationErrorsType = {
  email?: { message: string; fieldLabel: string };
  status?: { message: string; fieldLabel: string };
  scope?: { message: string; fieldLabel: string };
  expiresAt?: { message: string; fieldLabel: string };
  sentAt?: { message: string; fieldLabel: string };
  verifiedAt?: { message: string; fieldLabel: string };
  sentCount?: { message: string; fieldLabel: string };
  lastSentAt?: { message: string; fieldLabel: string };
  requestedBy?: { message: string; fieldLabel: string };
  sourceEntity?: { message: string; fieldLabel: string };
  sourceRecordId?: { message: string; fieldLabel: string };
  validationChanges?: { message: string; fieldLabel: string };
  validationSubmittedAt?: { message: string; fieldLabel: string };
};

export function EmailVerificationValuesInit(): EmailVerificationValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    email: "",
    status: "pending" as EmailVerificationStatus,
    scope: "" as EmailVerificationScope | "",
    expiresAt: null,
    sentAt: null,
    verifiedAt: null,
    sentCount: 0,
    lastSentAt: null,
    __requestedBy: "",
    showRequestedBy: null,
    sourceEntity: "",
    sourceRecordId: "",
    validationChanges: "{}",
    validationSubmittedAt: null,
  };
}

export type EmailVerificationType =
  | EmailVerification
  | EmailVerificationValuesType;

export function emailVerificationDisplayId(
  source: Partial<EmailVerification>,
  _langStore: LangStore
) {
  return source.email ?? _langStore.emptyStringSymbol;
}

export function getEmailVerificationValues(
  source: EmailVerificationType,
  _langStore: LangStore,
  _duplicated: boolean = false
): EmailVerificationValuesType {
  const entityValues = EmailVerificationValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : emailVerificationDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.email = source.email;
  entityValues.status = source.status;
  entityValues.scope = source.scope || ("" as EmailVerificationScope | "");
  entityValues.expiresAt = new Date(source.expiresAt);
  entityValues.sentAt = source.sentAt ? new Date(source.sentAt) : null;
  entityValues.verifiedAt = source.verifiedAt
    ? new Date(source.verifiedAt)
    : null;
  entityValues.sentCount = source.sentCount || 0;
  entityValues.lastSentAt = source.lastSentAt
    ? new Date(source.lastSentAt)
    : null;
  entityValues.showRequestedBy =
    "requestedBy" in source && source.requestedBy
      ? {
          id: source.requestedBy.id,
          displayId: userDisplayId(source.requestedBy, _langStore),
        }
      : "showRequestedBy" in source && source.showRequestedBy
      ? {
          id: source.showRequestedBy.id,
          displayId: source.showRequestedBy.displayId,
        }
      : null;
  entityValues.__requestedBy = entityValues.showRequestedBy?.displayId || "";
  entityValues.sourceEntity = source.sourceEntity || "";
  entityValues.sourceRecordId = source.sourceRecordId || "";
  entityValues.validationChanges = source.validationChanges || "";
  entityValues.validationSubmittedAt = source.validationSubmittedAt
    ? new Date(source.validationSubmittedAt)
    : null;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getEmailVerificationUpdateVariables(
  values: EmailVerificationValuesType
): EmailVerificationUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    email: values.email,
  };
}

export function getEmailVerificationCreateVariables(
  values: EmailVerificationValuesType
): EmailVerificationCreateMutationVariables {
  return {
    email: values.email,
  };
}
